// @flow
import Properties from "../../../Properties";
import { getInit, postInit, graphQLInit } from "../../global/types/constants";
import type { DispatchType } from "../../global/types/redux";
import {
  SoftwareNode,
  SoftwareVersionNode,
  SoftwareCompanyNode
} from "../types/GraphQlNodes";
import { apiFailCallbackHandle } from "../containers/ErrosDisplay";
import {
  GET_SOFTWARE_LIST,
  UPDATE_SOFTWARE_ITEM,
  GET_SOFTWARE_COMPANIES,
  GET_SOFTWARE_USERS
} from "../../global/actions/actionNames";

const root = Properties.api_root;

export const fetchSoftwares = (nextAction?: Function): * => (
  dispatch: DispatchType
): DispatchType => {
  const graphQl = `
  {
    softwares {
      ${SoftwareNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/software/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then((json: *): * => {
      dispatch({ type: GET_SOFTWARE_LIST, softwares: json.data.softwares });
      nextAction && nextAction(json.data.softwares);
    });
};

export const fetchSingleSoftware = (
  softwareId: string,
  nextAction?: Function
): * => (dispatch: DispatchType): DispatchType => {
  const graphQl = `
  {
    node(id: "${softwareId}"){
      ...on Software{
        ${SoftwareNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/software/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then((json: *): * => {
      dispatch({ type: UPDATE_SOFTWARE_ITEM, software: json.data.node });
      nextAction && nextAction(json.data.node);
    });
};

export const AddVersion = (
  softwareId: string,
  versionId: string,
  link: string,
  releaseDate: string,
  releaseNotes: string,
  isPublic: boolean,
  isCurrent: boolean,
  isBeta: boolean,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    createVersion(
      softwareId: "${softwareId}",
      versionId: "${versionId}",
      link: "${link}",
      releaseDate: "${releaseDate}",
      releaseNotes: "${releaseNotes}",
      isPublic: ${isPublic},
      isCurrent: ${isCurrent},
      isBeta: ${isBeta}
    ){
      ok
      errors {
        ... on SoftwareNotFound{
          message
          errorType
        }
        ... on VersionIdExist {
          message
          errorType
        }
        ... on CurrentVersionMustIsPublic {
          message
          errorType
        }
      }
      softwareVersion {
        ${SoftwareVersionNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/software/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createVersion;
        if (result.ok) {
          dispatch(fetchSingleSoftware(softwareId, doneCallback));
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const UpdateVersion = (
  softwareId: string,
  versionId: string,
  newVersionId: string,
  link: string,
  releaseDate: string,
  releaseNotes: string,
  isPublic: boolean,
  isCurrent: boolean,
  isBeta: boolean,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    updateVersion(
      softwareId: "${softwareId}",
      versionId: "${versionId}",
      newVersionId: "${newVersionId}",
      link: "${link}",
      releaseDate: "${releaseDate}",
      releaseNotes: "${releaseNotes}",
      isPublic: ${isPublic},
      isCurrent: ${isCurrent},
      isBeta: ${isBeta}
    ){
      ok
      errors {
        ... on SoftwareNotFound{
          message
          errorType
        }
        ... on VersionNotFound {
          message
          errorType
        }
        ... on CurrentVersionMustIsPublic {
          message
          errorType
        }
      }
      softwareVersion {
        ${SoftwareVersionNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/software/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateVersion;
        if (result.ok) {
          dispatch(fetchSingleSoftware(softwareId, doneCallback));
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const fetchCompanies = (nextAction?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    companies {
      ${SoftwareCompanyNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/software/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then((json: *): * => {
      dispatch({
        type: GET_SOFTWARE_COMPANIES,
        companies: json.data.companies
      });
      nextAction && nextAction(json.data.companies);
    });
};

export const fetctAllUsers = (callBack?: Function): * => (
  dispatch: DispatchType
): * => {
  return fetch(`${root}/auth/v2/users`, getInit)
    .then(response => response.json())
    .then(json => {
      dispatch({ type: GET_SOFTWARE_USERS, users: json.users });
      callBack && callBack(json.users);
    });
};

export const broadcastSoftware = (
  softwareId: string,
  emails: Array<string>,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const emailList = emails.map(email => `"${email}"`);

  const graphQl = `
  mutation {
    broadcastSoftware(
      softwareId: "${softwareId}",
      emails: [${emailList}]
    ){
      ok
      errors {
        ... on SoftwareNotFound{
          message
          errorType
        }
        ... on VersionNotFound{
          message
          errorType
        }
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/software/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.broadcastSoftware;
        if (result.ok) {
          doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export function getDownloadLinkUrl(
  softwareId: string,
  versionId: string,
  days?: number,
  doneCall: Function,
  failBack: Function
): * {
  return (dispatch: DispatchType) => {
    fetch(
      root +
        "/auth/v2/software/" +
        softwareId +
        "/versions/" +
        versionId +
        (days ? "?days=" + days : ""),
      getInit
    )
      .then(response => response.json())
      .then(function(json) {
        if (json.errors) {
          failBack && failBack(apiFailCallbackHandle(json.errors, "software"));
        } else {
          doneCall && doneCall(json.url);
        }
      })
      .catch(err => {
        failBack && failBack(apiFailCallbackHandle(err.message, "software"));
      });
  };
}
